import {
  Alert,
  BrandMark,
  Button,
  breakpoints,
  Chip,
  Col,
  DropdownMenu,
  Field,
  Icon,
  IconButton,
  Input,
  ListRow,
  Row,
  ScrollArea,
  Spinner,
  Stack,
  Status,
  Surface,
  Text,
  useConfirm,
  useTheme,
} from "@rally/ui-kit";
import { useState } from "react";
import { say } from "../localization/say.ts";
import { act } from "../net/rally.ts";
import {
  carriesItsOwnEndpoint,
  looksLikeABaseUrl,
  looksLikeACode,
  looksPasteable,
  type ProviderCardSubject,
  providerCalled,
} from "./a-provider.ts";
import { type HeldCredential, howItRuns, lastly, marksOf } from "./held.ts";
import { saidWhy, theFallbackShows, useSigningIn } from "./signing-in.ts";

type Verdict = { text: string; wrong: boolean } | null;

export type ProviderCardProps = {
  subject: ProviderCardSubject;
  held: HeldCredential[];
  onChanged: () => void;
};

export function ProviderCard({ subject, held, onChanged }: ProviderCardProps) {
  const theme = useTheme();
  const shownName = providerCalled(subject.provider);
  const [verdict, setVerdict] = useState<Verdict>(null);
  const [managing, setManaging] = useState(false);
  const [proving, setProving] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  const [pasting, setPasting] = useState<null | "add" | { replacing: string }>(null);
  const [secret, setSecret] = useState("");

  const [baseUrl, setBaseUrl] = useState("");
  const [model, setModel] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const signIn = useSigningIn(subject.signInAs ?? subject.provider, () => {
    setVerdict(null);
    onChanged();
  });

  function shut() {
    setSecret("");
    setPasting(null);
  }

  const needsAnEndpoint = subject.needsAnEndpoint === true;

  async function send(): Promise<void> {
    if (!looksPasteable(secret, subject.provider)) {
      return setVerdict({ text: say("providers.notAKey"), wrong: true });
    }

    if (needsAnEndpoint && pasting === "add") {
      if (!looksLikeABaseUrl(baseUrl)) {
        return setVerdict({ text: say("providers.notAnAddress"), wrong: true });
      }
      if (!model.trim()) return setVerdict({ text: say("providers.needsAModel"), wrong: true });
    }
    setBusy(true);
    setVerdict(null);
    try {
      if (pasting === "add") {
        await act("provider.add", {
          provider: subject.provider,
          secret,

          ...(needsAnEndpoint ? { baseUrl: baseUrl.trim(), model: model.trim(), label: baseUrl.trim() } : {}),
        });
      } else if (pasting) await act("provider.edit", { credential: pasting.replacing, secret });
      shut();
      onChanged();
    } catch (cause) {
      setVerdict({ text: saidWhy(cause), wrong: true });
    } finally {
      setSecret("");
      setBusy(false);
    }
  }

  async function prove(credential: string): Promise<void> {
    setVerdict(null);
    setProving(credential);
    try {
      const said = await act<{ answered: boolean | "unasked"; why: string | null }>("provider.test", {
        credential,
      });

      setVerdict({
        text:
          said.answered === true
            ? say("providers.answered")
            : said.answered === "unasked"
              ? say("providers.unasked")
              : (said.why ?? say("head.broke")),
        wrong: said.answered === false,
      });
      onChanged();
    } catch (cause) {
      setVerdict({ text: saidWhy(cause), wrong: true });
    } finally {
      setProving(null);
    }
  }

  async function drop(credential: string): Promise<void> {
    const sure = await ask({
      title: say("providers.removeTitle"),
      description: say("providers.removeMeans"),
      confirmLabel: say("providers.remove"),
      cancelLabel: say("providers.keep"),
      tone: "danger",
    });
    if (!sure) return;
    try {
      await act("provider.remove", { credential });
      onChanged();
    } catch (cause) {
      setVerdict({ text: saidWhy(cause), wrong: true });
    }
  }

  const step = signIn.state.step;
  const wrong = step === "refused" ? signIn.state.why : verdict?.wrong ? verdict.text : null;
  const tag = subject.provider.replace(/[^a-z]/g, "");

  const mark = subject.mark ? (
    <BrandMark name={subject.mark} size={22} tone="brand" />
  ) : (
    <Icon name={subject.icon ?? "hard-drive"} size={22} />
  );
  const first = held[0];
  return (
    <>
      <ListRow
        testID={`provider-card-${tag}`}
        lead={mark}
        title={shownName}
        value={first ? `${first.label} · ${lastly(first)}` : say(subject.whatKey)}
        action={
          <Row gap={2} align="center">
            {held.length > 0 ? (
              <Status tone="success">{say("providers.held")}</Status>
            ) : theme.isAtMost(breakpoints.phone) ? null : (
              <Text typeName="caption" color="text.tertiary">
                {say("providers.notSetUp")}
              </Text>
            )}
            <Icon name="chevron-right" size={16} color="text.tertiary" />
          </Row>
        }
        onPress={() => setManaging(true)}
      />
      <Surface
        kind="panel"
        width={520}
        visible={managing}
        onDismiss={() => setManaging(false)}
        title={shownName}
        testID={`provider-panel-${tag}`}
      >
        <ScrollArea contentContainerStyle={{ padding: theme.space[5] }}>
          <Col style={{ gap: theme.space[3] }}>
            {dialog}
            <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
              {subject.mark ? (
                <BrandMark name={subject.mark} size={22} tone="brand" />
              ) : (
                <Icon name={subject.icon ?? "hard-drive"} size={22} />
              )}
              <Text typeName="bodySemibold" color="text.primary" style={{ flex: 1 }}>
                {shownName}
              </Text>
              {held.length > 0 ? (
                <DropdownMenu
                  align="right"
                  width={220}
                  items={[
                    ...(subject.signsIn
                      ? [
                          {
                            label: say("providers.signIn")(subject.signInLabel ?? shownName),
                            icon: "log-in" as const,
                            onPress: () => void signIn.start(),
                          },
                        ]
                      : []),
                    ...(subject.takesAKey
                      ? [
                          {
                            label: say("providers.pasteAKey"),
                            icon: "key" as const,
                            onPress: () => {
                              setSecret("");
                              setPasting("add");
                            },
                          },
                        ]
                      : []),
                  ]}
                  trigger={
                    <Col style={{ pointerEvents: "none" }}>
                      <IconButton icon="plus" ariaLabel={say("providers.add")} />
                    </Col>
                  }
                />
              ) : null}
            </Col>
            <Text typeName="caption" color="text.secondary">
              {say(subject.whatKey)}
            </Text>

            {held.map((one) => (
              <Col
                key={one.id}
                testID={`provider-${one.id.slice(0, 8)}`}
                style={{
                  gap: theme.space[2],
                  paddingTop: theme.space[3],
                  borderTopWidth: 1,
                  borderTopColor: theme.color["border.default"],
                }}
              >
                <Row gap={2} align="center">
                  <Col style={{ flex: 1, minWidth: 0, gap: 2 }}>
                    <Text typeName="body" color="text.primary" numberOfLines={1}>
                      {one.label}
                    </Text>
                    <Row gap={1} wrap>
                      {marksOf(one).map((mark) => (
                        <Chip key={mark} size="sm" muted>
                          {mark}
                        </Chip>
                      ))}
                      {howItRuns(one) ? (
                        <Chip size="sm" muted icon={carriesItsOwnEndpoint(one.provider) ? "globe" : "key"}>
                          {howItRuns(one)}
                        </Chip>
                      ) : null}
                    </Row>
                  </Col>
                  <Status tone={one.provedAt ? "success" : "neutral"}>{lastly(one)}</Status>
                  <Button
                    size="sm"
                    variant="outline"
                    loading={proving === one.id}
                    onPress={() => void prove(one.id)}
                    testID="provider-test"
                  >
                    {say("providers.test")}
                  </Button>
                  <DropdownMenu
                    align="right"
                    width={220}
                    items={[
                      ...(subject.takesAKey
                        ? [
                            {
                              label: say("providers.replace"),
                              icon: "key" as const,
                              onPress: () => {
                                setSecret("");
                                setPasting({ replacing: one.id });
                              },
                            },
                          ]
                        : []),
                      {
                        label: say("providers.remove"),
                        icon: "trash" as const,
                        danger: true,
                        onPress: () => void drop(one.id),
                      },
                    ]}
                    trigger={
                      <Col testID="provider-more" style={{ pointerEvents: "none" }}>
                        <IconButton icon="more-horizontal" ariaLabel={say("providers.more")} />
                      </Col>
                    }
                  />
                </Row>
              </Col>
            ))}

            {wrong ? (
              <Col testID="provider-wrong">
                <Alert tone="danger">{wrong}</Alert>
              </Col>
            ) : null}
            {verdict && !verdict.wrong ? (
              <Col testID="provider-verdict">
                <Alert tone="success">{verdict.text}</Alert>
              </Col>
            ) : null}

            {step === "opening" ? <Spinner label={say("providers.opening")} /> : null}

            {step === "waiting" ? (
              <Stack gap={2} testID="provider-waiting">
                <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
                  <Spinner size="sm" />
                  <Text typeName="body" color="text.primary">
                    {say("providers.waiting")(subject.signInLabel ?? shownName)}
                  </Text>
                </Col>
                <Text typeName="caption" color="text.secondary">
                  {say("providers.waitingWhat")}
                </Text>
                {theFallbackShows(signIn.state) ? (
                  <Stack gap={2} testID="provider-fallback">
                    <Text typeName="caption" color="text.primary">
                      {say("providers.approvedNothing")}
                    </Text>
                    <Field label={say("providers.code")} hint={say("providers.codeHint")}>
                      <Input value={code} onChangeText={setCode} testID="provider-code" />
                    </Field>
                    <Col style={{ flexDirection: "row", gap: theme.space[2] }}>
                      <Button
                        size="sm"
                        loading={signIn.state.sending}
                        disabled={!looksLikeACode(code)}
                        onPress={() => void signIn.finish(code.trim())}
                        testID="provider-code-send"
                      >
                        {say("providers.finish")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onPress={signIn.forget}
                        testID="provider-signin-cancel"
                      >
                        {say("providers.cancel")}
                      </Button>
                    </Col>
                  </Stack>
                ) : null}
              </Stack>
            ) : null}

            {pasting ? (
              <Stack gap={2} testID="provider-paste">
                {needsAnEndpoint && pasting === "add" ? (
                  <>
                    <Field label={say("providers.baseUrl")} hint={say("providers.baseUrlHint")}>
                      <Input value={baseUrl} onChangeText={setBaseUrl} autoFocus testID="provider-base-url" />
                    </Field>
                    <Field label={say("providers.model")} hint={say("providers.modelHint")}>
                      <Input value={model} onChangeText={setModel} testID="provider-model" />
                    </Field>
                  </>
                ) : null}
                <Field
                  label={say("providers.key")}
                  hint={
                    subject.whereTheKeyIs
                      ? `${say("providers.keyHint")} ${say("providers.keyFrom")(subject.whereTheKeyIs)}`
                      : say("providers.keyHint")
                  }
                >
                  <Input
                    value={secret}
                    onChangeText={setSecret}
                    secureTextEntry
                    autoFocus={!(needsAnEndpoint && pasting === "add")}
                    testID="provider-key"
                  />
                </Field>
                <Col style={{ flexDirection: "row", gap: theme.space[2] }}>
                  <Button size="sm" loading={busy} onPress={() => void send()} testID="provider-key-save">
                    {say(pasting === "add" ? "providers.add" : "providers.replace")}
                  </Button>
                  <Button size="sm" variant="ghost" onPress={shut}>
                    {say("providers.cancel")}
                  </Button>
                </Col>
              </Stack>
            ) : null}

            {(step === "idle" || step === "refused") && held.length === 0 ? (
              <Row gap={2} wrap>
                {subject.signsIn ? (
                  <Button
                    variant="soft"
                    onPress={() => void signIn.start()}
                    testID={`provider-signin-${tag}`}
                    icon="log-in"
                  >
                    {say(step === "refused" ? "providers.tryAgain" : "providers.signIn")(
                      subject.signInLabel ?? shownName,
                    )}
                  </Button>
                ) : null}
                {subject.takesAKey && !pasting ? (
                  <Button
                    variant="soft"
                    size="md"
                    icon="key"
                    onPress={() => {
                      setSecret("");
                      setPasting("add");
                    }}
                    testID={`provider-paste-${tag}`}
                  >
                    {say(subject.signsIn ? "providers.pasteInstead" : "providers.pasteAKey")}
                  </Button>
                ) : null}
              </Row>
            ) : null}
          </Col>
        </ScrollArea>
      </Surface>
    </>
  );
}
